/**
 * Try the demo: name, email, optionally a firm name. Nothing more.
 *
 * The page never says whether an address already has an account. The server answers
 * the same way either way, and so does this screen: "check your inbox". Saying "you
 * already have an account" would let anybody test which addresses belong to a firm.
 *
 * No password here. The email carries a link that lets the person choose one, which is
 * also what proves the address is theirs; a password typed on this form would belong
 * to an address nobody has verified.
 */

import { requestDemo } from '@legalcrm/api';
import { Card, CardBody, Form, FormActions, FormInput, useZodForm } from '@legalcrm/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { apiClient } from '@/api/client';

const schema = z.object({
  full_name: z.string().trim().min(2, 'Enter your name'),
  email: z.string().trim().min(1, 'Enter your email address').email('Enter a valid email address'),
  firm_name: z.string().trim().max(120, 'Keep the firm name under 120 characters').optional(),
});

type DemoValues = z.infer<typeof schema>;

export const DEMO_DAYS = 15;

export function DemoSignupPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const form = useZodForm(schema, { defaultValues: { full_name: '', email: '', firm_name: '' } });

  const submit = async (values: DemoValues) => {
    setError(null);
    setSubmitting(true);
    try {
      await requestDemo(apiClient, {
        full_name: values.full_name,
        email: values.email,
        ...(values.firm_name ? { firm_name: values.firm_name } : {}),
      });
      setSentTo(values.email);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-hero px-4 py-10 text-hero-ink sm:py-16">
      <div className="mx-auto grid max-w-5xl items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <Link to="/" className="inline-flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-hero-ink font-display text-lg font-semibold text-hero">
              W
            </span>
            <span className="leading-tight">
              <span className="block text-sm font-semibold">WPCodie</span>
              <span className="block text-xs text-hero-muted">Legal CRM</span>
            </span>
          </Link>
          <h1 className="mt-10 font-display text-4xl leading-tight sm:text-5xl">
            Try Legal CRM with your own sample firm.
          </h1>
          <p className="mt-5 max-w-lg text-lg leading-relaxed text-hero-muted">
            We set up a private firm for you, already filled with sample matters, leads, documents,
            invoices and deadlines, so you can try everything straight away.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            {[
              `Free for ${DEMO_DAYS} days. No card needed.`,
              'Private to you. Nobody else sees your demo firm.',
              'Sample data only. Please do not upload real client files.',
            ].map((line) => (
              <li key={line} className="flex gap-3">
                <span
                  className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                  aria-hidden="true"
                />
                {line}
              </li>
            ))}
          </ul>
        </div>

        <Card className="text-ink">
          <CardBody>
            {sentTo ? (
              <div role="status" className="py-4">
                <h2 className="font-display text-2xl">Check your inbox</h2>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                  We are setting up your demo firm and will email{' '}
                  <span className="font-medium text-ink">{sentTo}</span> a link to choose your
                  password. It usually arrives within a couple of minutes.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                  Nothing there? Check your spam folder, or the email may already be on its way from
                  an earlier request.
                </p>
                <Link
                  to="/"
                  className="mt-6 inline-block text-sm font-medium text-primary hover:underline"
                >
                  Back to the overview
                </Link>
              </div>
            ) : (
              <Form form={form} error={error} ariaLabel="Start your demo" onSubmit={submit}>
                <div>
                  <h2 className="font-display text-2xl">Start your {DEMO_DAYS}-day demo</h2>
                  <p className="mt-1 text-sm text-ink-muted">
                    We will email you a link to choose your password.
                  </p>
                </div>
                <FormInput
                  form={form}
                  name="full_name"
                  label="Your name"
                  autoComplete="name"
                  required
                />
                <FormInput
                  form={form}
                  name="email"
                  label="Work email"
                  type="email"
                  autoComplete="email"
                  required
                />
                <FormInput
                  form={form}
                  name="firm_name"
                  label="Firm name"
                  hint="Optional. Your demo firm will use it."
                  autoComplete="organization"
                />
                <FormActions submitLabel="Email me my demo link" busy={submitting} />
                <p className="text-center text-xs text-ink-subtle">
                  Already have an account?{' '}
                  <Link to="/login" className="font-medium text-primary hover:underline">
                    Sign in
                  </Link>
                </p>
              </Form>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
